import httpClient, { ConexaResponse } from '@/services/httpClient'
import {
  IMedicalSpeciality,
  IMedicDays,
  IMedicHours,
  IMedicSearch,
  IHealthInsuranceCard,
  IHealthInsuranceCardResponse,
  IReScheduledProps,
  ResponseObject,
  ProfessionalsSlotsInterface,
  ProfessionalDataTimeSlotType,
  ProfessionalDataTimeSlotResponse,
  SpecialitiesAndCouncils,
  RequestImmediateAttendanceParams,
  CanRedirectToNewScheduleParams,
  Professionals,
  ProfessionalsBodyParams,
  ProfessionalScheduleParams,
  DaySlotSchedule,
  CheckProfessionalSlotsParams,
} from '@/modules/Attendance/submodules/Scheduled/scheduled-types'
import { Medic } from '@/modules/Attendance/classes/Medic'
import { readFileAsync } from '@/utils/file'
import { randomId } from '@/utils/fnUtils'
import { IScheduledAttendancePayload, IScheduleProps } from '../../types'
import { removeCache } from '@/utils/cache'
import GetSpecialitiesAndCouncilsData from './mappers/GetSpecialitiesAndCouncilsData'
import { autoCapitalize } from '@conexasaude/front-utils'
import { RequestImmediateAttendanceMapper } from './mappers/RequestImmediateAttendanceMapper'
import { autoCapitalizeName } from '@/utils/autoCapitalizeName'
import qs from 'qs'
import { CanRedirectToNewScheduleMapper } from './mappers/CanRedirectToNewScheduleMapper'
import ProfessionalsMapper from './mappers/ProfessionalsMapper'
import ProfessionalScheduleMapper from './mappers/ProfessionalScheduleMapper'

export default {
  async getMedicalSpecialties(): Promise<IMedicalSpeciality[]> {
    return httpClient
      .get('/integration/app-paciente/clinica/listar/especialidades')
      .then(res => {
        return res.data.object?.map(speciality => {
          return {
            id: speciality?.id,
            name: speciality?.nome,
            image: speciality?.imagem,
            description: speciality?.descricao,
            imageLink: speciality?.linkImagem,
            attendanceValue: speciality?.valorConsulta,
            council: speciality?.tipoConselho,
            allowTimeSlot: speciality?.flagPermiteEncaixe,
            allowQueue: speciality?.flagPermiteFilaEncaixe,
          }
        })
      })
  },
  async getTimeSlotBySpeciality(
    page: number,
    specialityId: string | number,
    itemsPerPage: number,
    professionalType: string,
  ): Promise<ProfessionalsSlotsInterface> {
    const { data } = await httpClient.post<ConexaResponse<ResponseObject>>(
      '/encaixe/buscar/por/especialidade',
      {
        pagina: page,
        idEspecialidade: specialityId,
        registrosPorPagina: itemsPerPage,
        tipoProfissional: professionalType,
      },
    )
    const slots = data?.object?.data?.map(speciality => ({
      councilUf: speciality?.conselhoUf,
      speciality: speciality?.especialidade,
      flagChat: speciality?.flagChat,
      flagAvaliableAttendance: speciality?.flagDisponivelAgendamento,
      flagOnline: speciality?.flagOnline,
      flagVideo: speciality?.flagVideo,
      flagVoice: speciality?.flagVoz,
      photo: speciality?.foto,
      id: speciality?.id,
      clinicId: speciality?.idClinica,
      specialityId: speciality?.idEspecialidade,
      name: autoCapitalize(speciality?.nome),
      fullNameWithSalutation: speciality?.nomeCompletoComSaudacao,
      councilNumber: speciality?.numeroConselho,
      nextAttendance: speciality?.proximoAtendimento,
      salutation: speciality?.saudacao,
      sex: speciality?.sexo,
      councilType: speciality?.tipoConselho,
      professionalType: {
        id: speciality?.tipoProfissional?.id,
        councilType: speciality?.tipoProfissional?.tipoConselho,
        description: speciality?.tipoProfissional?.descricao,
      },
      urlPhoto: speciality?.urlFoto,
      valueChatFmt: speciality?.valorChatFmt,
      valueVideo: speciality?.valorVideo,
      valueVideoFmt: speciality?.valorVideoFmt,
      valorVoice: speciality?.valorVoz,
      valorVoiceFmt: speciality?.valorVozFmt,
      nextScheduleHourIso: speciality?.proximoHorarioIso,
      scheduleHourIsClose: speciality?.isHorarioProximo,
    }))
    return {
      slots,
      updatedAt: data?.timestamp,
      hasMore: data?.object?.hasMore,
    }
  },

  async getMedicListBySpeciality(
    medicName = '',
    specialityId = 1,
    orderBy = 'DATE',
    page = 0,
    council = 'MEDICO',
  ): Promise<IMedicSearch[]> {
    return httpClient
      .post(
        specialityId
          ? '/integration/app-paciente/profissional/buscar/por/especialidade'
          : '/integration/app-paciente/profissional/buscar/por/nome',
        {
          idEspecialidade: specialityId,
          orderby: orderBy,
          pagina: page,
          nome: medicName.trim(),
          tipoProfissional: council.toUpperCase(),
        },
      )
      .then(res => {
        return res.data.object?.map(medic => {
          return {
            id: medic.id,
            name: medic.nome,
            salutation: medic.saudacao,
            photo: medic.urlFoto,
            council: medic.tipoConselho,
            councilNumber: medic.numeroConselho,
            councilState: medic.conselhoUf,
            specialityId: medic.tipoProfissional.id,
            speciality: medic.especialidade,
            isCorporate: medic.flagProfissionalCorporate,
          }
        })
      })
  },

  async getMedicProfileImmediate(id: string): Promise<Medic> {
    return httpClient
      .get(
        `/integration/app-paciente/profissional/detalhar/pronto-atendimento/${id}`,
      )
      .then(({ data }) => {
        return new Medic(
          data.object.idProfissional,
          data.object.nome,
          data.object.urlFoto,
          data.object.nomeEspecialidade,
          data.object.conselho,
          data.object.idEspecialidade,
          data.object.qualificacoes?.map(i => ({
            graduationYear: i.anoFormacao,
            course: i.nomeCurso,
            institution: i.nomeInstituicao,
          })),
          data.object.sobre,
          (data.object.avaliacoes = {
            total: data?.object?.avaliacoes?.total,
          }),
          data.object.temas,
          data.object.titulo,
          (data.object.sessao = {
            paymentCurrency: data?.object?.sessao?.moedaPagamento,
            time: data?.object?.sessao?.tempo,
            amount: data?.object?.sessao?.valor,
            inPersonAmount: data?.object?.sessao?.valorPresencial,
          }),
          data.object.cidade,
          data.object.estado,
          data.object.flagProfissionalCorporate,
          data.object.formacao,
          data.object.abordagem,
        )
      })
  },

  async getMedicProfile(id: string): Promise<Medic> {
    return httpClient
      .get(`/integration/app-paciente/profissional/detalhar/${id}`)
      .then(({ data }) => {
        return new Medic(
          data.object.idProfissional,
          autoCapitalizeName(data?.object?.nome),
          data.object.urlFoto,
          data.object.nomeEspecialidade,
          data.object.conselho,
          data.object.idEspecialidade,
          data.object.qualificacoes?.map(i => ({
            graduationYear: i.anoFormacao,
            course: i.nomeCurso,
            institution: i.nomeInstituicao,
          })),
          data.object.sobre,
          (data.object.avaliacoes = {
            total: data?.object?.avaliacoes?.total,
          }),
          data.object.temas,
          data.object.titulo,
          (data.object.sessao = {
            paymentCurrency: data?.object?.sessao?.moedaPagamento,
            time: data?.object?.sessao?.tempo,
            amount: data?.object?.sessao?.valor,
            inPersonAmount: data?.object?.sessao?.valorPresencial,
          }),
          data.object.cidade,
          data.object.estado,
          data.object.flagProfissionalCorporate,
          data.object.formacao,
          data.object.abordagem,
        )
      })
  },

  async getMedicDays(
    id: string,
    year: string,
    month: string,
  ): Promise<IMedicDays> {
    return httpClient
      .get(
        `/integration/app-paciente/profissional/datas-disponiveis?idProfissional=${id}&ano=${year}&mes=${month}`,
      )
      .then(({ data }) => data.object.datasDisponiveis)
  },

  async getMedicHours(id: string, date: string): Promise<IMedicHours> {
    return httpClient
      .get(
        `/integration/app-paciente/profissional/horarios-livres?idProfissional=${id}&data=${date}`,
      )
      .then(
        ({
          data: {
            object: { MADRUGADA, MANHA, TARDE, NOITE },
          },
        }) => {
          const periods: IMedicHours = {
            dawn: MADRUGADA,
            morning: MANHA,
            afternoon: TARDE,
            night: NOITE,
          }
          return periods
        },
      )
  },

  async setFirstConsult(id: number, ip: string): Promise<void> {
    return httpClient.post(
      '/integration/app-paciente/atendimento/resposta-primeiro-atendimento',
      {
        idProfissional: id,
        isPrimeiroAtendimento: true,
        ipResposta: ip,
      },
    )
  },

  async getFirstConsult(id: number): Promise<boolean> {
    return httpClient
      .get(
        `/integration/app-paciente/atendimento/primeiro-atendimento/respondido/${id}`,
      )
      .then(d => d.data.object)
  },

  async schedule({
    date,
    professionalId,
    email,
    cellphone,
    healthInsuranceCardId,
    screeningId,
    paymentObject,
    discountCoupon,
    scheduleId,
    hasAnyServiceEnabledFlag,
    isSchedulingAPS,
    ctaScheduling,
    origin,
  }: IScheduleProps): Promise<number> {
    const payload: IScheduledAttendancePayload = {
      atendimento: {
        idTriagem: screeningId,
        dataHoraAgendamento: date,
        idProfissional: professionalId,
        cartaoConvenio: {
          id: healthInsuranceCardId,
        },
        aps: !!isSchedulingAPS,
      },
      contato: {
        email: email,
        celular: cellphone,
      },
    }
    if (ctaScheduling) {
      payload.atendimento.ctaAgendamento = ctaScheduling
      removeCache('ctaAgendamento')
    }
    if (paymentObject) {
      payload.atendimento.pagamento = paymentObject
    }

    if (discountCoupon) {
      payload.atendimento.cupomDesconto = discountCoupon
    }

    if (scheduleId) {
      payload.atendimento.scheduleId = scheduleId
    }

    if (!hasAnyServiceEnabledFlag) {
      payload.atendimento.agendamentoAtribuido = true
    }

    if (origin) {
      payload.atendimento.origemAgendamento = origin
    }

    return httpClient
      .post('/integration/app-paciente/atendimento/novo/agendado/v2', payload)
      .then(d => d.data.object)
  },

  async reschedule({
    date,
    token,
    screeningId,
    scheduleId,
  }: IReScheduledProps): Promise<number> {
    return httpClient
      .post('/integration/app-paciente/atendimento/reagendar', {
        data: date,
        token,
        idTriagem: screeningId,
        scheduleId: scheduleId,
      })
      .then(d => d.data.object)
  },

  async getHealthInsuranceCards(): Promise<IHealthInsuranceCard[]> {
    const { data } = await httpClient.get<
      ConexaResponse<IHealthInsuranceCardResponse[]>
    >('/integration/app-paciente/convenios?o=web')

    const healthInsuranceCards: IHealthInsuranceCard[] = data?.object?.map(
      ({ id, numeroCartao, paciente, convenio }) => {
        return {
          id,
          cardNumber: numeroCartao,
          patient: paciente,
          covenant: convenio,
        }
      },
    )

    return healthInsuranceCards || []
  },

  async sendAttachment(scheduledId: string, file: File): Promise<boolean> {
    const stringFile = (await readFileAsync(file)) as string
    const b64 = stringFile.substring(stringFile.indexOf('base64,') + 7)
    return httpClient
      .post(
        `/integration/app-paciente/atendimento/upload/anexo/${scheduledId}`,
        {
          conteudoBase64: b64,
          idAnexo: randomId('anexo'),
          legenda: file.name,
          nomeExtensao: file.name.split('.').pop(),
        },
      )
      .then(d => d.data.object)
  },

  async deleteAttachment(id: number | string): Promise<boolean> {
    return httpClient
      .get('/integration/app-paciente/atendimento/excluir/anexo/' + id)
      .then(d => d.data.object)
  },

  async sendProfessionalWarning(id: number | string): Promise<boolean> {
    const response = await httpClient.get(
      `/integration/app-paciente/sala-espera-interativa/notificar-profissional/${id}`,
    )

    return response?.data?.object
  },

  async getProfessionalProfileTimeSlot(
    professionalId: string | number,
  ): Promise<ProfessionalDataTimeSlotType> {
    const { data } = await httpClient.get<
      ConexaResponse<ProfessionalDataTimeSlotResponse>
    >(`/encaixe/profissional/perfil/${professionalId}`)

    return {
      name: autoCapitalize(data?.object?.nome),
      aboutMe: data?.object?.sobreMim,
      councilNumber: data?.object?.numeroConselho,
      councilType: data?.object?.tipoConselho,
      councilUf: data?.object?.ufConselho,
      sessionTime: data?.object?.tempoDeSessao,
    }
  },

  async getSpecialitiesAndCouncils(): Promise<SpecialitiesAndCouncils[]> {
    const res = await httpClient.get(
      '/integration/app-paciente/clinica/listar/servicos-especialidades/agendado/v2',
    )
    return GetSpecialitiesAndCouncilsData.toDomain(res?.data?.object)
  },

  async requestImmediateAttendance(
    params: RequestImmediateAttendanceParams,
  ): Promise<string> {
    return httpClient
      .post(
        `/integration/app-paciente/atendimento/novo/imediato`,
        RequestImmediateAttendanceMapper.toPersistence(params),
      )
      .then(d => d.data.object)
  },

  async canRedirectToNewSchedule(
    params: CanRedirectToNewScheduleParams,
  ): Promise<boolean> {
    const response = await httpClient.get(
      `/v2/professionals/search/slot-schedule/can-show`,
      {
        params: CanRedirectToNewScheduleMapper.toPersistence(params),
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'comma', encode: false })
        },
      },
    )
    return response?.data?.object
  },

  async getProfessionals(data: ProfessionalsBodyParams) {
    const requestData = ProfessionalsMapper.toPersistence(data)

    const response = await httpClient.get<ConexaResponse<Professionals>>(
      `v1/clinics/${data.clinicId}/professionals`,
      {
        params: requestData,
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      },
    )

    return {
      total: response.data.object?.total,
      items: response.data.object?.items?.map(ProfessionalsMapper.toDomain),
    }
  },

  async checkProfessionalAvailability(
    professionalId: number,
  ): Promise<boolean> {
    const response = await httpClient.get<ConexaResponse<boolean>>(
      `/integration/app-paciente/profissional/agenda/disponivel?idProfissional=${professionalId}`,
    )

    return response.data.object
  },

  async getProfessionalSchedule(params: ProfessionalScheduleParams) {
    const {
      data: { object },
    } = await httpClient.get<ConexaResponse<DaySlotSchedule[]>>(
      `v1/clinics/${params.clinicId}/professionals/${params.professionalId}?startDate=${params.startDate}&endDate=${params.endDate}`,
      {
        params: {
          isPsychologistAlreadyMigrated: params?.isPsychologistAlreadyMigrated,
        },
      },
    )

    return object.map(ProfessionalScheduleMapper.toDomain)
  },

  async getProfessionalIsNotified(professionalId: number): Promise<boolean> {
    const response = await httpClient.get<ConexaResponse<boolean>>(
      `/integration/app-paciente/profissional/notificacao-novos-horarios/${professionalId}/notificado`,
    )

    return response?.data?.object
  },

  async requestScheduleNotification(professionalId: number) {
    const response = await httpClient.post<ConexaResponse<boolean>>(
      `/integration/app-paciente/profissional/solicitar-horario?idProfissional=${professionalId}`,
    )

    return response.data.object
  },

  async checkProfessionalSlots({
    slotId,
    professionalId,
    dateAndHour,
  }: CheckProfessionalSlotsParams) {
    const response = await httpClient.post<ConexaResponse<boolean>>(
      `/integration/app-paciente/atendimento/slots/profissional/${professionalId}/reservar`,
      {
        slotId,
        data: dateAndHour,
      },
    )

    return response.data.object
  },
}
